
import { computed, defineComponent, PropType, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { DownloadOutline as ExportIcon } from '@vicons/ionicons5';
import { Survey } from '@/types';
import { useAuth0 } from '@/auth';
import responseService from '@/services/response-service';
import { createResponseCSV, downloadCSV, EXPORT_GROUPS, ExportColumn, getExportColumns, normalizeCSVFilename } from '@/utils/exportResponseToCSV';
import { loadExportSelection, saveExportSelection } from '@/utils/exportColumnPreferences';

// som-header sits at z-index 3010, above Naive UI's default modal layer, and would cover the top of a tall modal.
const MODAL_Z_INDEX = 3020;

const optionLabel = (column: ExportColumn): string => (column.variant ? `${column.field} (${column.variant})` : column.field);

export default defineComponent({
  name: 'ExportCSVModal',
  components: { ExportIcon },
  props: {
    survey: { type: Object as PropType<Survey | null>, default: null },
    showModal: { type: Boolean, default: false }
  },
  emits: ['update:showModal'],
  setup(props, { emit }) {
    const auth = useAuth0();
    const userId = computed(() => auth.state.user?.sub);
    const filename = ref('');
    const selectedIds = ref<string[]>([]);
    const exporting = ref(false);
    const submitError = ref('');
    const columns = computed(() => (props.survey ? getExportColumns(props.survey) : []));
    const validFilename = computed(() => !!normalizeCSVFilename(filename.value));
    const filenameStatus = computed(() => (validFilename.value ? 'default' : 'error'));
    const canExport = computed(() => !!props.survey && validFilename.value && selectedIds.value.length > 0 && !exporting.value);
    const groups = computed(() => EXPORT_GROUPS.map((label) => ({ label, columns: columns.value.filter((column) => column.group === label) })));
    watch(
      () => [props.showModal, props.survey, userId.value],
      () => {
        exporting.value = false;
        submitError.value = '';
        if (props.showModal && props.survey) {
          filename.value = `${props.survey.name}-${DateTime.now().toISODate()}.csv`;
          selectedIds.value = loadExportSelection(columns.value, userId.value, props.survey.id);
        }
      },
      { immediate: true, flush: 'sync' }
    );

    const setSelected = (id: string, checked: boolean) => {
      const selected = new Set(selectedIds.value);
      if (checked) selected.add(id);
      else selected.delete(id);
      selectedIds.value = [...selected];
    };
    const onClose = (show: boolean) => {
      if (!exporting.value) emit('update:showModal', show);
    };
    const onExport = async () => {
      if (!canExport.value || !props.survey) return;
      const { survey } = props;
      const exportColumns = columns.value;
      const selection = [...selectedIds.value];
      const exportFilename = filename.value;
      const exportUser = userId.value;
      exporting.value = true;
      submitError.value = '';
      try {
        const responses = await responseService.getList(survey.id);
        downloadCSV(createResponseCSV(responses, exportColumns, selection), exportFilename);
        saveExportSelection(exportColumns, selection, exportUser, survey.id);
        emit('update:showModal', false);
      } catch {
        submitError.value = 'Could not export responses. Please try again.';
      } finally {
        exporting.value = false;
      }
    };
    return { MODAL_Z_INDEX, optionLabel, filename, selectedIds, exporting, submitError, validFilename, filenameStatus, canExport, groups, setSelected, onClose, onExport };
  }
});
